import React from 'react';
import { Images, Sparkles, Wand2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { useImageNavigation } from '@/hooks/image-generator/use-image-navigation';

interface EmptyStateViewProps {
  showFavorites: boolean;
  showOnlyUserImages: boolean;
  searchTerm: string;
  parsedWidth?: number;
  parsedHeight?: number;
}

export const EmptyStateView: React.FC<EmptyStateViewProps> = ({ 
  showFavorites, 
  showOnlyUserImages,
  searchTerm,
  parsedWidth,
  parsedHeight
}) => {
  // Use the same navigation hook as the header button
  const { navigateToGenerateImage } = useImageNavigation();
  // Determine the main message based on the current filter state
  const getMainMessage = () => {
    if (showFavorites) return "No favorite images found";
    if (showOnlyUserImages) return "No images created yet";
    return "No images found matching your selection";
  };

  // Determine the subtitle based on the current filter state
  const getSubtitle = () => {
    if (showFavorites) {
      return "Mark images as favorites to see them here, or generate new images to expand your collection.";
    }
    if (showOnlyUserImages) {
      return "Start creating beautiful AI-generated images to build your personal gallery.";
    }
    return "Try adjusting your filters or generate new images to expand your collection.";
  };

  return (
    <motion.div 
      className="text-center py-16 px-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Animated icon container */}
      <motion.div 
        className="relative mx-auto mb-6 w-20 h-20"
        whileHover={{ scale: 1.05 }}
        transition={{ duration: 0.2 }}
      >
        {/* Background glow */}
        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-full blur-xl"></div>
        
        {/* Main icon background */}
        <div className="relative w-full h-full bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
          <Images className="h-10 w-10 text-white" />
          
          {/* Floating sparkles */}
          <motion.div
            className="absolute -top-2 -right-2"
            animate={{ 
              scale: [1, 1.2, 1],
              rotate: [0, 180, 360]
            }}
            transition={{ 
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            <Sparkles className="h-4 w-4 text-yellow-400" />
          </motion.div>
        </div>
      </motion.div>

      {/* Main heading */}
      <motion.h3 
        className="text-2xl font-bold mb-3 bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.5 }}
      >
        {getMainMessage()}
      </motion.h3>
      
      {/* Subtitle */}
      <motion.p 
        className="text-gray-600 dark:text-gray-400 mb-8 max-w-md mx-auto leading-relaxed"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3, duration: 0.5 }}
      >
        {getSubtitle()}
      </motion.p>

      {/* Call-to-action button - only show if not in favorites mode */}
      {!showFavorites && (
        <motion.button
          className="group relative overflow-hidden bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.5 }}
          whileHover={{ y: -2 }}
          whileTap={{ scale: 0.98 }}
          onClick={navigateToGenerateImage}
        >
          {/* Button shine effect */}
          <span className="absolute inset-0 w-0 h-full bg-white opacity-20 transition-all duration-500 group-hover:w-full"></span>
          
          {/* Button content */}
          <span className="relative flex items-center gap-2">
            <Wand2 className="h-5 w-5" />
            New Image
          </span>
        </motion.button>
      )}

      {/* Additional helper text */}
      <motion.div 
        className="mt-6 text-sm text-gray-500 dark:text-gray-500"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5, duration: 0.5 }}
      >
        <p>Or use the sidebar to adjust your filters →</p>
      </motion.div>
    </motion.div>
  );
};
